/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Children, useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Box,
    HStack,
    useStyleConfig,
    Button,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useIntl} from 'react-intl'
import SizeGuide from '../size-guide'
import {PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'

const DIRECTIONS = {
    FORWARD: 1,
    BACKWARD: -1
}

/**
 * SwatchGroup allows you to create a list of swatches
 * Each Swatch is a link with will direct to a href passed to them
 */
const SwatchGroup = (props) => {
    const {
        ariaLabel,
        displayName,
        children,
        label = '',
        value,
        handleChange = noop,
        c_sizeChart
    } = props
    const intl = useIntl()

    const {isOpen, onOpen, onClose} = useDisclosure()

    const [sizeGuideContentId, setSizeGuideContentId] = useState('')

    const styles = useStyleConfig('SwatchGroup')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const wrapperRef = useRef(null)

    // Handle keyboard navigation.
    const onKeyDown = useCallback(
        (e) => {
            const {key} = e
            const move = (direction = DIRECTIONS.FORWARD) => {
                let index = selectedIndex + direction // forward = +1 backwards = -1
                index = (selectedIndex + direction) % children.length // keep number in bounds of array with modulus
                index = index < 0 ? children.length - Math.abs(index) : Math.abs(index) // We we are dealing with a negative we have to invert the index

                // Get a reference to the newly selected swatch as we are going to focus it later.
                const swatchEl = wrapperRef?.current?.children[index]

                // Set the new index that is always in the arrays range.
                setSelectedIndex(index)

                // Behave like a radio button a focus the new swatch.
                swatchEl?.focus()
            }

            switch (key) {
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault()
                    move(DIRECTIONS.BACKWARD)
                    break
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault()
                    move(DIRECTIONS.FORWARD)
                    break
                default:
                    break
            }
        },
        [selectedIndex]
    )

    const handleOpenSizeGuide = () => {
        onOpen()
        if (c_sizeChart) setSizeGuideContentId(c_sizeChart)
    }

    // Initialize the component state on mount, this includes the selected index value.
    useEffect(() => {
        if (!value) {
            return
        }
        const childrenArray = Children.toArray(children)
        const index = childrenArray.findIndex(({props}) => props?.value === value)

        setSelectedIndex(index)
    }, [])

    // Whenever the selected index changes ensure that we call the change handler.
    useEffect(() => {
        const childrenArray = Children.toArray(children)
        const newValue = childrenArray[selectedIndex]?.props?.value

        handleChange(newValue)
    }, [selectedIndex])

    return (
        <Box onKeyDown={onKeyDown}>
            <Flex {...styles.swatchGroup} role="radiogroup" aria-label={ariaLabel || label}>
                {label && (
                    <HStack {...styles.swatchLabel}>
                        <Box fontWeight="semibold" {...styles.swatchLabelText}>
                            <FormattedMessage
                                id="swatch_group.selected.label"
                                defaultMessage="{label}:"
                                values={{label}}
                            />
                            {/* <Box>{displayName}</Box> */}
                        </Box>
                        {(label == 'Size' || label == 'Waist') && c_sizeChart && (
                            <Box fontWeight="semibold" {...styles.swatchLabelText}>
                                <Button
                                    onClick={handleOpenSizeGuide}
                                    title={intl.formatMessage({
                                        id: 'product_detail.size_guide',
                                        defaultMessage: 'Size Guide'
                                    })}
                                    variant="unstyled"
                                    {...styles.sizeGuideButton}
                                >
                                    {intl.formatMessage({
                                        id: 'product_detail.size_guide',
                                        defaultMessage: 'Size Guide'
                                    })}
                                </Button>
                            </Box>
                        )}
                    </HStack>
                )}
                <Flex ref={wrapperRef} {...styles.swatchesWrapper}>
                    {Children.toArray(children).map((child, index) => {
                        let selected = child?.props?.value === value

                        if (
                            child.props.variantName.toLowerCase() ===
                            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
                        ) {
                            selected = child.props.href.includes(
                                PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
                            )
                        }

                        return React.cloneElement(child, {
                            handleSelect: handleChange,
                            selected,
                            isFocusable: value ? selected : index === 0
                        })
                    })}
                </Flex>

                {/* size guide pop up */}
                {sizeGuideContentId && (
                    <SizeGuide
                        isOpen={isOpen}
                        onClose={onClose}
                        sizeGuideContentId={sizeGuideContentId}
                    />
                )}
            </Flex>
        </Box>
    )
}

SwatchGroup.displayName = 'SwatchGroup'

SwatchGroup.propTypes = {
    ariaLabel: PropTypes.string,
    label: PropTypes.string,
    displayName: PropTypes.string,
    children: PropTypes.node,
    handleChange: PropTypes.func,
    value: PropTypes.string,
    c_sizeChart: PropTypes.string
}

export default SwatchGroup
